import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { GridColDef } from '@mui/x-data-grid';
import { useAuth } from "../utils/AuthContext";
import Layout from "./Layout";
import { Environment } from "../utils/Environment";
import { DataGrid } from '@mui/x-data-grid';
import Checkbox from '@mui/material/Checkbox';
import Button from '@mui/material/Button';

const apiUrl = Environment.getApiUrl();
interface User {
  id: number;
  username: string;
  client_id?: string;
  client_name?: string;
  email?: string;
  job_id?: string;
  sub_status?: boolean;
  sub_expiry?: string;
  role?: string;
}

const UserDashboard: React.FC = () => {
  // const username = localStorage.getItem("userName") || "";
  const { isLoggedIn, userName, logout } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>([]);
  const [subscriptionStatus, setSubscriptionStatus] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("access_token");
        const res = await fetch(`${apiUrl}/getCurrentUserRole`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        const userData = await res.json();
        if (userData.role === 'admin') {
          setIsAdmin(true);
          const usersRes = await fetch(`${apiUrl}/getUsers`, {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          const usersData = await usersRes.json();
          setUsers(usersData.users ?? usersData);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (!isAdmin) return null;

  // Toggle handler for checkboxes
  const handleToggle = (client_id?: string) => {
    if (!client_id) return;
    setSelectedClientIds((prev) =>
      prev.includes(client_id)
        ? prev.filter(id => id !== client_id)
        : [...prev, client_id]
    );
  };

  // Approve button handler
  const approveSubscription = async () => {
    const token = localStorage.getItem("access_token");
    const response = await fetch(`${apiUrl}/approveSubcriptionForClient`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ client_ids: selectedClientIds }),
    });
    const data = await response.json();
    // Optionally handle response, update UI, etc.
    if (data.subscriptionStatus === false) {
      setSubscriptionStatus(false);
    } else {
      setSubscriptionStatus(true);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'select',
      headerName: 'Select',
      width: 80,
      renderCell: (params) => (
        <Checkbox
          checked={selectedClientIds.includes(params.row.client_id)}
          onChange={() => handleToggle(params.row.client_id)}
          disabled={!params.row.client_id}
        />
      ),
      sortable: false,
      filterable: false,
    },
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'username', headerName: 'Username', width: 130 },
    { field: 'client_id', headerName: 'Client ID', width: 130 },
    { field: 'client_name', headerName: 'Client Name', width: 130 },
    { field: 'email', headerName: 'Email', width: 200 },
    { field: 'job_id', headerName: 'Job ID', width: 130 },
    { field: 'role', headerName: 'Role', width: 100 },
    { field: 'sub_status', headerName: 'Subscription Status', width: 200 },
    { field: 'sub_expiry', headerName: 'Subscription Expiration Date', width: 200 }
  ];

  return (
    <Layout isLoggedIn={isLoggedIn} onLogout={logout} userName={userName}>
      <Box
        sx={{
          width: '100vw',
          height: '100vh',
          p: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h4" sx={{ mb: 2 }}>Admin Dashboard</Typography>
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={users}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 10 } }
            }}
            pageSizeOptions={[10, 25, 50]}
            loading={loading}
            disableRowSelectionOnClick
            filterMode="client"
            sx={{ background: '#fff' }}
          />
        </Box>
        <Button
          variant="contained"
          color="primary"
          sx={{ mt: 3 }}
          disabled={selectedClientIds.length === 0}
          onClick={approveSubscription}
        >
          Approve Selected
        </Button>
        {subscriptionStatus && (
          <Typography sx={{ mt: 2, color: 'success.main', fontWeight: 500 }}>
            Subscription(s) approved successfully!
          </Typography>
        )}
      </Box>
    </Layout>
  );
};

export default UserDashboard;